/**
 * Camera viewfinder for the key-transfer screens.
 *
 * Extracted because protocol v3 needs a scanner on *both* devices: the target
 * scans the sealed seed, the source scans the target's public key. Native uses
 * `expo-camera`; web falls back to `WebQRScanner`, which offers a webcam or an
 * uploaded image.
 */

import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { CameraView, useCameraPermissions } from 'expo-camera';
import { useTheme } from '../theme/ThemeContext';
import { useThemedStyles } from '../theme/useThemedStyles';
import WebQRScanner from './WebQRScanner';
import type { Theme } from '../theme/types';

interface QRScannerViewProps {
  title: string;
  subtitle: string;
  onScan: (data: string) => void;
  onBack: () => void;
  isProcessing: boolean;
  processingLabel: string;
  error: string | null;
  onDismissError: () => void;
  /** Prefixes every testID, e.g. "transfer-target". */
  testIDPrefix: string;
}

export default function QRScannerView({
  title,
  subtitle,
  onScan,
  onBack,
  isProcessing,
  processingLabel,
  error,
  onDismissError,
  testIDPrefix,
}: QRScannerViewProps) {
  const [permission, requestPermission] = useCameraPermissions();
  const styles = useThemedStyles(createStyles);
  const { colors } = useTheme();

  // Web — browser webcam / file upload
  if (Platform.OS === 'web') {
    return (
      <View style={styles.container}>
        <WebQRScanner
          onScan={onScan}
          onBack={onBack}
          isProcessing={isProcessing}
          error={error}
        />
      </View>
    );
  }

  if (!permission) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color={colors.textOnPrimary} />
      </View>
    );
  }

  if (!permission.granted) {
    return (
      <View style={styles.container}>
        <View style={styles.card}>
          <View style={styles.iconContainer}>
            <Text style={styles.iconText}>{'\u{1F4F7}'}</Text>
          </View>
          <Text style={styles.cardTitle}>Camera Access Needed</Text>
          <Text style={styles.cardSubtitle}>
            We need camera access to scan the QR code from your other device.
            Your camera is only used for scanning — no photos are taken or stored.
          </Text>
          <TouchableOpacity
            style={styles.grantButton}
            onPress={requestPermission}
            activeOpacity={0.7}
            accessibilityLabel="Grant camera access"
            accessibilityRole="button"
            testID={`${testIDPrefix}.grant-camera-button`}
          >
            <Text style={styles.grantButtonText}>Grant Camera Access</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.backButton}
            onPress={onBack}
            activeOpacity={0.7}
            accessibilityLabel="Go back"
            accessibilityRole="button"
            testID={`${testIDPrefix}.back-button-permission`}
          >
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.scannerContainer}>
      <CameraView
        style={StyleSheet.absoluteFill}
        facing="back"
        barcodeScannerSettings={{ barcodeTypes: ['qr'] }}
        onBarcodeScanned={({ data }: { data: string }) => onScan(data)}
      />

      <View style={styles.overlay}>
        <View style={styles.overlayTop}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onBack}
            activeOpacity={0.7}
            accessibilityLabel="Close scanner"
            accessibilityRole="button"
            testID={`${testIDPrefix}.close-button`}
          >
            <Text style={styles.closeButtonText} accessible={false}>{'✕'}</Text>
          </TouchableOpacity>
          <Text style={styles.overlayTitle}>{title}</Text>
          <Text style={styles.overlaySubtitle}>{subtitle}</Text>
        </View>

        <View style={styles.cutoutRow}>
          <View style={styles.overlaySide} />
          <View style={styles.cutout}>
            <View style={[styles.corner, styles.cornerTL]} />
            <View style={[styles.corner, styles.cornerTR]} />
            <View style={[styles.corner, styles.cornerBL]} />
            <View style={[styles.corner, styles.cornerBR]} />
          </View>
          <View style={styles.overlaySide} />
        </View>

        <View style={styles.overlayBottom}>
          {isProcessing && (
            <View style={styles.processingBox}>
              <ActivityIndicator size="small" color={colors.textOnPrimary} />
              <Text style={styles.processingText}> {processingLabel}</Text>
            </View>
          )}
          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
              <TouchableOpacity
                onPress={onDismissError}
                activeOpacity={0.7}
                accessibilityLabel="Try scanning again"
                accessibilityRole="button"
                testID={`${testIDPrefix}.retry-button`}
              >
                <Text style={styles.retryText}>Tap to try again</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}

const CUTOUT_SIZE = 250;

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1 as const,
    backgroundColor: theme.colors.brandedBackground,
    justifyContent: 'center' as const,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.primaryBg,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    alignSelf: 'center' as const,
    marginBottom: 16,
  },
  iconText: { fontSize: 28 },
  cardTitle: {
    fontSize: 20,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  cardSubtitle: {
    fontSize: 14,
    color: theme.colors.textMuted,
    textAlign: 'center' as const,
    marginBottom: 20,
    lineHeight: 20,
  },
  grantButton: {
    backgroundColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
    marginBottom: 10,
  },
  grantButtonText: {
    color: theme.colors.textOnPrimary,
    fontSize: 16,
    fontWeight: '700' as const,
  },
  backButton: {
    borderWidth: 1,
    borderColor: theme.colors.borderMedium,
    borderRadius: 10,
    paddingVertical: 12,
    alignItems: 'center' as const,
  },
  backButtonText: {
    color: theme.colors.textSecondary,
    fontSize: 16,
    fontWeight: '600' as const,
  },
  // Camera overlay colors stay fixed (not theme-dependent)
  scannerContainer: {
    flex: 1 as const,
    backgroundColor: '#000',
  },
  overlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  overlayTop: {
    flex: 1 as const,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center' as const,
    justifyContent: 'flex-end' as const,
    paddingBottom: 24,
  },
  closeButton: {
    position: 'absolute' as const,
    top: 60,
    right: 20,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '700' as const,
  },
  overlayTitle: {
    color: '#ffffff',
    fontSize: 20,
    fontWeight: '700' as const,
    marginBottom: 6,
  },
  overlaySubtitle: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 14,
    textAlign: 'center' as const,
    paddingHorizontal: 40,
  },
  cutoutRow: {
    flexDirection: 'row' as const,
    height: CUTOUT_SIZE,
  },
  overlaySide: {
    flex: 1 as const,
    backgroundColor: 'rgba(0,0,0,0.6)',
  },
  cutout: {
    width: CUTOUT_SIZE,
    height: CUTOUT_SIZE,
  },
  corner: {
    position: 'absolute' as const,
    width: 24,
    height: 24,
    borderColor: '#ffffff',
  },
  cornerTL: { top: 0, left: 0, borderTopWidth: 3, borderLeftWidth: 3 },
  cornerTR: { top: 0, right: 0, borderTopWidth: 3, borderRightWidth: 3 },
  cornerBL: { bottom: 0, left: 0, borderBottomWidth: 3, borderLeftWidth: 3 },
  cornerBR: { bottom: 0, right: 0, borderBottomWidth: 3, borderRightWidth: 3 },
  overlayBottom: {
    flex: 1 as const,
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center' as const,
    paddingTop: 24,
  },
  processingBox: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  processingText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600' as const,
  },
  errorBox: {
    backgroundColor: 'rgba(153,27,27,0.9)',
    borderRadius: 10,
    padding: 14,
    marginHorizontal: 24,
    alignItems: 'center' as const,
  },
  errorText: {
    color: '#ffffff',
    fontSize: 14,
    lineHeight: 20,
    textAlign: 'center' as const,
    marginBottom: 8,
  },
  retryText: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 13,
    fontWeight: '600' as const,
    textDecorationLine: 'underline' as const,
  },
});
